import { Badge, BodyText, Tooltip } from '@postscript/components';
import { DateTime } from 'luxon';
import React from 'react';

import { SubscriberAIEligibility } from '../hooks/useSubscriberAiEligibility';

interface AiEligibleBadgeProps {
  // Eligibility query result. `undefined`/`null` (request failed or returned
  // nothing) and `eligible === null` (profile-service unreachable) both render
  // as "Unknown".
  data?: SubscriberAIEligibility | null;
  isLoading: boolean;
  // Unique id tying the badge to its "Last consent" tooltip. Must be unique
  // per rendered instance so multiple cards don't collide.
  tooltipId: string;
}

// Consent-based AI eligibility badge shared by the /conversations Subscriber
// Profile card and the Message Details drawer. Encapsulates the loading →
// unknown → true/false state machine plus the optional "Last consent" tooltip
// so both surfaces stay in lockstep.
export const AiEligibleBadge: React.FC<AiEligibleBadgeProps> = ({
  data,
  isLoading,
  tooltipId,
}) => {
  if (isLoading) {
    return <BodyText size="x-small">--</BodyText>;
  }

  if (!data || data.eligible === null) {
    return <Badge variant="neutral">Unknown</Badge>;
  }

  const tooltipContent = data.lastConsentAt
    ? `Last consent: ${DateTime.fromISO(data.lastConsentAt).toLocaleString(
        DateTime.DATETIME_MED,
      )}`
    : null;

  const tooltipProps = tooltipContent
    ? {
        tabIndex: 0,
        'data-tip': true,
        'data-for': tooltipId,
        'aria-describedby': tooltipId,
      }
    : {};

  return (
    <>
      {data.eligible ? (
        <Badge variant="success" {...tooltipProps}>
          True
        </Badge>
      ) : (
        <Badge variant="warning" {...tooltipProps}>
          False
        </Badge>
      )}
      {tooltipContent && (
        <Tooltip id={tooltipId} place="top" size="small">
          {tooltipContent}
        </Tooltip>
      )}
    </>
  );
};

export default AiEligibleBadge;
